import { SITE_DOMAIN } from '@/config/brandConfig'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { VipDashboard } from '@/components/vip/VipDashboard'
import { AddressForm } from '@/features/mailing/AddressForm'
import { submitAddress, getAddress } from '@/features/mailing/mailingService'
import { checkVipRegistration, registerVipInterest } from '@/services/vipService'

const ACCESS_PASSWORD = '111100'
const ACCESS_URLS = [`www.${SITE_DOMAIN}/vip`, `${SITE_DOMAIN}/vip`]

type Step = 'loading' | 'choice' | 'address-form' | 'address-sent' | 'password' | 'dashboard'

export function StatusPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [step, setStep] = useState<Step>('loading')
  const [passwordInput, setPasswordInput] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // On mount: check if already VIP registered → dashboard, otherwise → choice
  useEffect(() => {
    if (authLoading || !isAuthenticated) return

    checkVipRegistration()
      .then((result) => {
        setStep(result.registered ? 'dashboard' : 'choice')
      })
      .catch(() => {
        setStep('choice')
      })
  }, [authLoading, isAuthenticated])

  // "Request for Activation" — check if already has address
  const handleRequestActivation = async () => {
    setStep('loading')
    try {
      const address = await getAddress()
      if (address) {
        setStep('address-sent')
      } else {
        setStep('address-form')
      }
    } catch {
      setStep('address-form')
    }
  }

  // Address form submit
  const handleAddressSubmit = async (data: Parameters<typeof submitAddress>[0]) => {
    setIsSubmitting(true)
    try {
      const result = await submitAddress(data)
      if (result.success) {
        setStep('address-sent')
      } else if (result.validation_error) {
        setError(result.reason || 'Address validation failed. Please check your address.')
      } else {
        setError('Failed to submit address. Please try again.')
      }
    } catch {
      setError('Failed to submit address. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // "I Got My Letter" — password/URL check
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = passwordInput.trim().toLowerCase()

    if (trimmed === ACCESS_PASSWORD || ACCESS_URLS.includes(trimmed)) {
      setError('')
      setIsSubmitting(true)
      try {
        await registerVipInterest()
        setStep('dashboard')
      } catch {
        setError('Something went wrong. Please try again.')
      } finally {
        setIsSubmitting(false)
      }
    } else {
      setError('Invalid password or URL')
    }
  }

  // Auth loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#ece6dd] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6b7850]" />
      </div>
    )
  }

  // Not logged in
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#ece6dd] flex items-center justify-center px-4">
        <div className="bg-white/80 rounded-3xl shadow-lg p-8 max-w-sm w-full text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            You are currently not logged in
          </h1>
          <p className="text-sm text-gray-700 mb-8 leading-relaxed">
            Please return to this page after logging in to begin.
          </p>
          <button
            onClick={() => navigate('/login?returnTo=/status')}
            className="w-full py-3 bg-[#6b7850] text-white font-semibold rounded-xl hover:bg-[#5c5b44] transition-colors"
          >
            Log In
          </button>
        </div>
      </div>
    )
  }

  // Loading (checking registration)
  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-[#ece6dd] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6b7850]" />
      </div>
    )
  }

  // Dashboard
  if (step === 'dashboard') {
    return (
      <div className="min-h-screen bg-[#ece6dd] flex flex-col items-center px-4 py-8">
        <div className="w-full max-w-sm">
          <VipDashboard />
          <div className="mt-5 text-center">
            <button
              onClick={() => navigate('/welcome')}
              className="text-sm text-gray-700 hover:text-gray-900 font-medium transition-colors cursor-pointer"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Choice screen: two buttons
  if (step === 'choice') {
    return (
      <div className="min-h-screen bg-[#ece6dd] flex flex-col items-center justify-center px-4">
        <div className="bg-white/80 rounded-3xl shadow-lg p-8 max-w-sm w-full text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Activate Your Status
          </h1>
          <p className="text-sm text-gray-700 mb-8 leading-relaxed">
            To view your status you will need to request for an activation letter.
          </p>

          <div className="space-y-3">
            <button
              onClick={handleRequestActivation}
              className="w-full py-3 bg-[#6b7850] text-white font-semibold rounded-xl hover:bg-[#5c5b44] transition-colors"
            >
              Request for Activation
            </button>
            <button
              onClick={() => setStep('password')}
              className="w-full py-3 bg-white text-gray-700 font-semibold rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              I Got My Letter
            </button>
          </div>
        </div>
        <button
          onClick={() => navigate('/welcome')}
          className="mt-5 text-sm text-gray-700 hover:text-gray-900 font-medium transition-colors cursor-pointer"
        >
          Back to Home
        </button>
      </div>
    )
  }

  // Address form
  if (step === 'address-form') {
    return (
      <div className="min-h-screen bg-[#ece6dd] flex items-center justify-center px-4 py-8">
        <div className="bg-white/80 rounded-3xl shadow-lg p-6 max-w-sm w-full">
          <h1 className="text-xl font-bold text-gray-900 mb-1 text-center">
            Request for Activation
          </h1>
          <p className="text-sm text-gray-700 mb-5 text-center leading-relaxed">
            Enter your mailing address and we'll send you a letter with your activation code.
          </p>

          {error && (
            <p className="text-xs text-red-500 mb-3 text-center">{error}</p>
          )}

          <AddressForm
            onSubmit={handleAddressSubmit}
            onCancel={() => { setError(''); setStep('choice') }}
            submitLabel="Submit Address"
            isSubmitting={isSubmitting}
          />
        </div>
      </div>
    )
  }

  // Address sent confirmation
  if (step === 'address-sent') {
    return (
      <div className="min-h-screen bg-[#ece6dd] flex items-center justify-center px-4">
        <div className="bg-white/80 rounded-3xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="text-4xl mb-4">&#x2709;&#xFE0F;</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            You're All Set!
          </h1>
          <p className="text-sm text-gray-700 mb-6 leading-relaxed">
            A letter will be sent to you in 2-7 days with your activation info. Once you receive it, come back and tap "I Got My Letter" to activate.
          </p>
          <button
            onClick={() => setStep('choice')}
            className="w-full py-3 bg-[#6b7850] text-white font-semibold rounded-xl hover:bg-[#5c5b44] transition-colors"
          >
            Back
          </button>
        </div>
      </div>
    )
  }

  // Password / URL input
  if (step === 'password') {
    return (
      <div className="min-h-screen bg-[#ece6dd] flex items-center justify-center px-4">
        <div className="bg-white/80 rounded-3xl shadow-lg p-8 max-w-sm w-full text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Enter Your Code
          </h1>
          <p className="text-sm text-gray-700 mb-6 leading-relaxed">
            Enter the password or URL from your letter to activate your status.
          </p>
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="text"
              value={passwordInput}
              onChange={(e) => { setPasswordInput(e.target.value); setError('') }}
              placeholder="Password or URL from your letter"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-center text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#6b7850] focus:ring-1 focus:ring-[#6b7850] mb-3"
              autoFocus
              disabled={isSubmitting}
            />
            {error && (
              <p className="text-xs text-red-500 mb-3">{error}</p>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-[#6b7850] text-white font-semibold rounded-xl hover:bg-[#5c5b44] transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Activating...' : 'Activate'}
            </button>
          </form>
          <button
            onClick={() => { setError(''); setPasswordInput(''); setStep('choice') }}
            className="mt-4 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            Back
          </button>
        </div>
      </div>
    )
  }

  return null
}
